import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, CheckCircle2, XCircle, ArrowLeft, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [done, setDone] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    setLoading(true); setStatus(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      setStep(2);
      setResendIn(60);
      const sms = data.channel === "sms";
      const accepted = sms ? data.delivery === "SMS_SENT" : data.delivery === "SENT";
      setStatus(!accepted
        ? { ok: false, text: sms ? "OTP delivery failed — the SMS provider rejected the message. Use RESEND OTP or try your registered email instead." : "OTP delivery failed — the email provider rejected the send. Use RESEND OTP to try again, or contact support@10x-global.com." }
        : { ok: true, text: sms ? "OTP sent to your registered mobile number. Please check your SMS." : "OTP sent to your registered email. Check your inbox and spam folder. The code is valid for 10 minutes." });
    } catch (err) {
      setStatus({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendIn > 0 || loading) return;
    setLoading(true); setStatus(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      setOtp(""); setResendIn(60);
      const sms = data.channel === "sms";
      const accepted = sms ? data.delivery === "SMS_SENT" : data.delivery === "SENT";
      setStatus(!accepted
        ? { ok: false, text: sms ? "OTP delivery failed again — the SMS provider rejected the message. Try your registered email instead." : "OTP delivery failed again — the email provider rejected the send. Try again shortly or contact support@10x-global.com." }
        : { ok: true, text: sms ? "New OTP sent to your registered mobile number — all previous codes are now invalid." : "New OTP sent to your registered email — all previous codes are now invalid." });
    } catch (err) {
      setStatus({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setStatus({ ok: false, text: "Passwords do not match" }); return; }
    setLoading(true); setStatus(null);
    try {
      await api.post("/auth/reset-password", { identifier, otp, new_password: password });
      setDone(true);
      toast.success("Password reset successfully");
    } catch (err) {
      const code = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      setStatus({
        ok: false,
        text: code === 401 ? `Invalid OTP — ${detail}. Attempts are limited; check the latest code in your email.`
          : code === 410 ? "OTP expired — codes are valid for 10 minutes. Use RESEND OTP for a new code."
          : code === 429 ? detail
          : code === 404 ? "No active reset request — request a new OTP first."
          : detail,
      });
    } finally { setLoading(false); }
  };

  const statusBanner = status && (
    <div className={`flex items-start gap-2 text-xs rounded-sm px-3.5 py-2.5 border ${status.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}
      data-testid={status.ok ? "fp-status-success" : "fp-status-error"}>
      {status.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
      <span>{status.text}</span>
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="forgot-page">
      <div className="text-center mb-8">
        <span className="inline-flex w-12 h-12 rounded-sm bg-gold items-center justify-center font-display font-black text-navy-950"><KeyRound size={22} /></span>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-100 mt-5">RESET YOUR PASSWORD</h1>
        <p className="text-sm text-slate-500 mt-2">We will send a one-time code (OTP) to your registered email — enter your registered email or mobile number.</p>
      </div>

      {done ? (
        <div className="card-surface p-8 text-center" data-testid="fp-success-panel">
          <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-4" />
          <p className="font-display text-lg font-bold text-slate-100">PASSWORD RESET SUCCESSFULLY</p>
          <p className="text-sm text-slate-400 mt-2">Sign in with your new password.</p>
          <Link to="/login" data-testid="fp-back-to-login" className="btn-gold mt-6 inline-flex">BACK TO SIGN IN</Link>
        </div>
      ) : step === 1 ? (
        <form onSubmit={sendOtp} className="card-surface p-7 space-y-5" data-testid="fp-request-form">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Registered Email or Mobile</label>
            <input required value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@company.com or +91 …"
              data-testid="fp-identifier-input" className="input-dark" />
          </div>
          {statusBanner}
          <button type="submit" disabled={loading || !identifier.trim()} data-testid="fp-send-otp-btn" className="btn-gold w-full disabled:opacity-60">
            {loading ? <Loader2 size={15} className="animate-spin" /> : "SEND OTP"}
          </button>
          <p className="text-center text-xs text-slate-500">Remembered it? <Link to="/login" className="text-gold font-semibold" data-testid="fp-login-link">Back to Sign In</Link></p>
        </form>
      ) : (
        <form onSubmit={reset} className="card-surface p-7 space-y-5" data-testid="fp-reset-form">
          {statusBanner}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">One-Time Code (OTP)</label>
            <input required inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code"
              data-testid="fp-otp-input" className="input-dark tracking-[0.4em] font-mono" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">New Password</label>
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters"
              data-testid="fp-new-password-input" className="input-dark" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Confirm New Password</label>
            <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password"
              data-testid="fp-confirm-password-input" className="input-dark" />
          </div>
          <button type="submit" disabled={loading || otp.length !== 6} data-testid="fp-reset-btn" className="btn-gold w-full disabled:opacity-60">
            {loading ? <Loader2 size={15} className="animate-spin" /> : "RESET PASSWORD"}
          </button>
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={() => { setStep(1); setOtp(""); setStatus(null); }} data-testid="fp-start-over"
              className="text-slate-500 hover:text-gold transition-colors inline-flex items-center gap-1.5"><ArrowLeft size={12} /> Change email / mobile</button>
            <button type="button" onClick={resend} disabled={resendIn > 0 || loading} data-testid="fp-resend-btn"
              className="text-gold font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {resendIn > 0 ? `Resend available in ${resendIn}s` : "RESEND OTP"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
